<template>
  <div v-if="hasPatients">
    <PriorHeader />
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1 class="text-primary weight-600 pb-4">Search Patient by MRN</h1>
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="mrnSearch"
            label="MRN"
            placeholder="Enter MRN to search"
            clearable
            @keyup.enter="searchPatients"
            @update:model-value="onMRNChange">
            <template #prepend-inner>
              <v-btn icon="mdi-magnify" variant="text" @click="searchPatients" />
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="auto" class="d-flex align-center">
          <v-btn-lh @click="searchPatients">Search</v-btn-lh>
        </v-col>
      </v-row>
      <v-row v-if="searchLoading">
        <v-col cols="12">
          <v-progress-linear indeterminate class="mb-4" />
        </v-col>
      </v-row>
      <v-row v-if="searchResults.length > 0 || selectedPatient">
        <v-col cols="12">
          <v-card>
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ selectedPatient ? "Selected Patient" : "Search Results - Select a Patient" }}</span>
              <v-btn-lh v-if="selectedPatient" variant="text" size="small" @click="clearSelection">
                Clear Selection
              </v-btn-lh>
            </v-card-title>
            <v-card-text>
              <v-radio-group v-model="selectedPatientId" @update:model-value="onPatientSelectionChange">
                <v-list lines="two" density="comfortable">
                  <v-list-item
                    v-for="patient in searchResults"
                    :key="patient.id"
                    :class="{ 'bg-primary-lighten-5': selectedPatientId === patient.id }"
                    @click="onSelectPatient(patient)">
                    <template #prepend>
                      <v-radio :value="patient.id" />
                    </template>
                    <v-list-item-title> {{ patient.firstName }} {{ patient.lastName }} </v-list-item-title>
                    <v-list-item-subtitle>
                      <span v-if="patient.dob">{{ convertDateToMMDDYYYY(patient.dob) }}</span>
                      <span v-else>DOB: N/A</span>
                      <span v-if="patient.phoneNumber || patient.extraData?.phoneNumber">
                        • Phone: {{ patient.phoneNumber || patient.extraData?.phoneNumber }}
                      </span>
                      <span v-else> • Phone: N/A</span>
                      <span v-if="patient.address"> • {{ patient.address }}</span>
                      <span v-if="patient.externalId"> • MRN: {{ patient.externalId }}</span>
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-radio-group>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-else-if="hasSearched && !searchLoading">
        <v-col cols="12">
          <v-alert type="info" variant="tonal">No patients found matching the MRN.</v-alert>
        </v-col>
      </v-row>
      <v-row v-if="intakeRecordsLoading">
        <v-col cols="12">
          <v-progress-linear indeterminate class="mb-4" />
        </v-col>
      </v-row>
      <v-row v-if="selectedPatient && !intakeRecordsLoading && intakeRecords">
        <v-col cols="12">
          <v-card>
            <v-card-title class="text-h6">Intake Records</v-card-title>
            <v-card-text>
              <div v-if="intakeRecords.documents && intakeRecords.documents.length > 0">
                <div v-for="doc in intakeRecords.documents" :key="doc.id" class="mb-3">
                  <div
                    class="d-flex align-center justify-space-between"
                    style="cursor: pointer"
                    @click="toggleDocumentSelection(doc.id, !selectedDocuments.includes(doc.id))">
                    <div class="d-flex align-center">
                      <v-checkbox
                        :model-value="selectedDocuments.includes(doc.id)"
                        class="mr-3"
                        @update:model-value="toggleDocumentSelection(doc.id, $event)"
                        @click.stop />
                      <div class="text-subtitle-1 d-flex align-center mb-6">
                        <span v-if="doc.documentTypes && doc.documentTypes.length > 0">
                          <span v-for="(docType, index) in doc.documentTypes" :key="index">
                            <span v-if="index > 0">, </span>{{ docType }}
                          </span>
                          <span v-if="doc.createdOn"> received on {{ new Date(doc.createdOn).toLocaleString() }}</span>
                        </span>
                        <span v-else-if="doc.createdOn">
                          Document received at {{ new Date(doc.createdOn).toLocaleString() }}
                        </span>
                        <span v-else>Document</span>
                      </div>
                    </div>
                    <v-btn-lh
                      v-if="doc.inputPresignedUrl"
                      class="mb-6"
                      variant="text"
                      size="small"
                      @click.stop="openPreview(doc)">
                      Quick Look
                    </v-btn-lh>
                  </div>
                  <!-- Insurance Cards for this document (Max 1 Selection) -->
                  <div v-if="doc.insuranceCards && doc.insuranceCards.length > 0" class="ml-8">
                    <div class="d-flex align-center justify-space-between">
                      <p class="text-subtitle-2 mb-0">Select Insurance</p>
                      <v-btn-lh
                        v-if="selectedInsuranceCards[doc.id]"
                        variant="text"
                        size="small"
                        @click="clearInsuranceSelection(doc.id)">
                        Clear Selection
                      </v-btn-lh>
                    </div>
                    <v-radio-group
                      :model-value="selectedInsuranceCards[doc.id]"
                      @update:model-value="selectedInsuranceCards[doc.id] = $event">
                      <v-list lines="two" density="compact">
                        <v-list-item
                          v-for="card in doc.insuranceCards"
                          :key="card.id"
                          style="cursor: pointer"
                          @click="handleInsuranceClick(doc.id, card.id, $event)">
                          <template #prepend>
                            <v-radio :value="card.id" @click.stop="handleInsuranceClick(doc.id, card.id, $event)" />
                          </template>
                          <v-list-item-title>{{ card.payorName || "Insurance Card" }}</v-list-item-title>
                          <v-list-item-subtitle>
                            <span v-if="card.memberId"> • <strong>Member ID:</strong> {{ card.memberId }}</span>
                            <span v-if="card.rxBin"> • <strong>BIN:</strong> {{ card.rxBin }}</span>
                            <span v-if="card.rxPcn"> • <strong>PCN:</strong> {{ card.rxPcn }}</span>
                            <span v-if="card.rxGrp || card.Group">
                              • <strong>Group:</strong> {{ card.rxGrp ? card.rxGrp : card.Group }}
                            </span>
                            <span v-if="card.insurancePlan"> • <strong>Plan:</strong> {{ card.insurancePlan }}</span>
                            <span v-if="card.benefitType"> • <strong>Type:</strong> {{ card.benefitType }}</span>
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-radio-group>
                  </div>
                  <v-alert v-else type="info" variant="tonal" class="ml-8">
                    No insurance cards found for this document.
                  </v-alert>
                  <!-- Medications for this document (Max 1 Selection) -->
                  <div v-if="getDocumentMedications(doc).length > 0" class="ml-8">
                    <div class="d-flex align-center justify-space-between">
                      <p class="text-subtitle-2 mb-0">Select Medication</p>
                      <v-btn-lh
                        v-if="selectedMedications[doc.id]"
                        variant="text"
                        size="small"
                        @click="clearMedicationSelection(doc.id)">
                        Clear Selection
                      </v-btn-lh>
                    </div>
                    <v-radio-group
                      :model-value="selectedMedications[doc.id]"
                      @update:model-value="selectedMedications[doc.id] = $event">
                      <v-list lines="two" density="compact">
                        <v-list-item
                          v-for="therapy in getDocumentMedications(doc)"
                          :key="therapy.name"
                          style="cursor: pointer"
                          @click="handleMedicationClick(doc.id, therapy.name, $event)">
                          <template #prepend>
                            <v-radio
                              :value="therapy.name"
                              @click.stop="handleMedicationClick(doc.id, therapy.name, $event)" />
                          </template>
                          <v-list-item-title>
                            {{ therapy.name || "Therapy Administration" }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            <span v-if="therapy.dosage"> • <strong>Dosage:</strong> {{ therapy.dosage }}</span>
                            <span v-if="therapy.frequency || (therapy.frequencyValue && therapy.frequencyUnit)">
                              • <strong>Frequency:</strong>
                              {{
                                therapy.frequency
                                  ? therapy.frequency
                                  : therapy.frequencyValue + " " + therapy.frequencyUnit
                              }}
                            </span>
                            <span v-if="therapy.notes"> • <strong>Notes:</strong> {{ therapy.notes }}</span>
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-radio-group>
                  </div>
                  <v-alert v-else type="info" variant="tonal" class="ml-8">
                    No medications found for this document.
                  </v-alert>
                </div>
              </div>
              <div
                v-if="
                  intakeRecords.documents?.length > 0 &&
                  intakeRecords.documents.length < (intakeRecords.totalCount ?? 0)
                "
                class="mt-3">
                <v-btn variant="outlined" :loading="intakeRecordsLoadingMore" @click="loadMoreIntakeRecords">
                  Load more (showing {{ intakeRecords.documents.length }} of {{ intakeRecords.totalCount }})
                </v-btn>
              </div>
              <v-alert v-else-if="!intakeRecords.documents?.length" type="info" variant="tonal">
                No documents found for this patient.
              </v-alert>
            </v-card-text>
            <v-btn
              v-if="
                Object.keys(selectedMedications).length > 0 ||
                Object.keys(selectedInsuranceCards).length > 0 ||
                selectedDocuments.length > 0
              "
              class="ma-4"
              @click="handlePrepopulate">
              Start New Prior Auth with Selected Data
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <PriorFooter />
    <v-dialog v-model="showPreviewDialog" max-width="90%" width="90%" height="90%">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Document Preview</span>
          <v-btn icon="mdi-close" variant="text" @click="closePreview" />
        </v-card-title>
        <v-card-text style="height: 80vh; padding: 0; position: relative">
          <v-progress-linear v-if="previewLoading" indeterminate class="mb-0" />
          <object
            v-if="previewDocumentUrl && !previewLoading"
            :data="previewDocumentUrl"
            type="application/pdf"
            style="width: 100%; height: 100%; border: none">
            <iframe :src="previewDocumentUrl" style="width: 100%; height: 100%; border: none" frameborder="0" />
            <p>
              Your browser does not support PDFs. <a :href="previewDocumentUrl" target="_blank">Download the PDF</a>.
            </p>
          </object>
          <v-alert v-if="previewError" type="error" variant="tonal" class="ma-4">
            Failed to load document preview. Please try again.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { intakeService } from "@/services/intakeService";
import { patientService } from "@/services/patientService";
import { convertDateToMMDDYYYY, fetchDiagnosisNameFromICD10 } from "@/utils";
import { usaStatesListItems } from "@/utils/usaStates";
import { mainServices } from "@/services/mainServices";
import { getMedicationDefaultsForName, applyMedicationDefaultsToTarget } from "@/utils/clientConfig";
import { useRequirementsStore } from "@/stores";

const emit = defineEmits(["prepopulate-order-info"]);
const requirementsStore = useRequirementsStore();

const mrnSearch = ref("");
const searchResults = ref([]);
const searchLoading = ref(false);
const hasSearched = ref(false);
const selectedPatient = ref(null);
const selectedPatientId = ref(null);
const intakeRecords = ref(null);
const intakeRecordsLoading = ref(false);
const selectedDocuments = ref([]);
const selectedInsuranceCards = ref({}); // { documentId: insuranceCardId }
const selectedMedications = ref({}); // { orderFormId: [therapyAdministrationId1, therapyAdministrationId2, ...] }
const showPreviewDialog = ref(false);
const previewDocumentUrl = ref(null);
const previewLoading = ref(false);
const previewError = ref(false);
const hasPatients = ref(false);

// Check if the client company has any patients in the database
// Uses count endpoint to determine if patient search should be shown
const checkIfPatientsExist = async () => {
  try {
    const response = await patientService.getPatientsCount();
    hasPatients.value = (response.count || 0) > 0;
  } catch (error) {
    // If check fails, default to hiding the component to be safe
    hasPatients.value = false;
  }
};

onMounted(async () => {
  await checkIfPatientsExist();
});

const onMRNChange = () => {
  if (!mrnSearch.value) {
    searchResults.value = [];
    hasSearched.value = false;
    selectedPatient.value = null;
    selectedPatientId.value = null;
    intakeRecords.value = null;
    clearSelections();
  }
};

const searchPatients = async () => {
  if (!mrnSearch.value?.trim()) {
    return;
  }

  searchLoading.value = true;
  hasSearched.value = true;
  selectedPatient.value = null;
  selectedPatientId.value = null;
  intakeRecords.value = null;
  clearSelections();
  try {
    const response = await patientService.searchPatientsByMRN(mrnSearch.value.trim());
    searchResults.value = response?.results || [];
  } catch (error) {
    searchResults.value = [];
  } finally {
    searchLoading.value = false;
  }
};

const intakeRecordsLoadingMore = ref(false);

const fetchIntakeRecords = async (patientId, options = {}) => {
  const { page = 1, append = false } = options;
  if (!patientId) {
    intakeRecords.value = null;
    return;
  }

  if (append) {
    intakeRecordsLoadingMore.value = true;
  } else {
    intakeRecordsLoading.value = true;
  }
  try {
    const response = await intakeService.getPatientIntakeRecords(patientId, {
      page,
      pageSize: 5,
    });
    const data = response || {
      documents: [],
      totalCount: 0,
      page: 1,
      pageSize: 5,
    };
    const docs = data.documents || [];
    if (append && intakeRecords.value?.documents) {
      intakeRecords.value = {
        ...intakeRecords.value,
        documents: [...intakeRecords.value.documents, ...docs],
        totalCount: data.totalCount ?? intakeRecords.value.totalCount,
        page: data.page ?? page,
        pageSize: data.pageSize ?? intakeRecords.value.pageSize,
      };
    } else {
      intakeRecords.value = {
        documents: docs,
        totalCount: data.totalCount ?? 0,
        page: data.page ?? 1,
        pageSize: data.pageSize ?? 5,
      };
    }
  } catch (error) {
    if (!append) {
      intakeRecords.value = {
        documents: [],
        totalCount: 0,
        page: 1,
        pageSize: 5,
      };
    }
  } finally {
    intakeRecordsLoading.value = false;
    intakeRecordsLoadingMore.value = false;
  }
};

const loadMoreIntakeRecords = () => {
  if (!selectedPatientId.value || !intakeRecords.value) return;
  const nextPage = (intakeRecords.value.page ?? 1) + 1;
  if (intakeRecords.value.documents?.length >= (intakeRecords.value.totalCount ?? 0)) return;
  fetchIntakeRecords(selectedPatientId.value, { page: nextPage, append: true });
};

const onSelectPatient = (patient) => {
  selectedPatientId.value = patient.id;
  selectedPatient.value = patient;
};

const onPatientSelectionChange = (patientId) => {
  const patient = searchResults.value.find((p) => p.id === patientId);
  if (patient) {
    selectedPatient.value = patient;
  }
};

const clearSelection = () => {
  selectedPatient.value = null;
  selectedPatientId.value = null;
  intakeRecords.value = null;
  clearSelections();
};

/** Reset search terms and results (used when starting a new submission). */
const resetSearch = () => {
  mrnSearch.value = "";
  searchResults.value = [];
  hasSearched.value = false;
  clearSelection();
};

const clearSelections = () => {
  selectedDocuments.value = [];
  selectedInsuranceCards.value = {};
  selectedMedications.value = {};
};

const toggleDocumentSelection = (docId, isSelected) => {
  if (isSelected) {
    if (!selectedDocuments.value.includes(docId)) {
      selectedDocuments.value.push(docId);
    }
  } else {
    selectedDocuments.value = selectedDocuments.value.filter((id) => id !== docId);
    // Clear selections for this document
    delete selectedInsuranceCards.value[docId];
    delete selectedMedications.value[docId];
  }
};

const getDocumentMedications = (doc) => {
  const medications = [];
  if (doc.orderForms && doc.orderForms.length > 0) {
    doc.orderForms.forEach((form) => {
      if (form.therapyAdministration && form.therapyAdministration.length > 0) {
        medications.push(...form.therapyAdministration);
      }
    });
  }
  return medications;
};

const handleInsuranceClick = (docId, cardId, event) => {
  // If clicking the same card that's already selected, clear the selection
  const currentValue = selectedInsuranceCards.value[docId];
  if (currentValue === cardId) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearInsuranceSelection(docId);
  } else {
    // Select the card
    selectedInsuranceCards.value[docId] = cardId;
  }
};

const handleMedicationClick = (docId, therapyName, event) => {
  // If clicking the same medication that's already selected, clear the selection
  const currentValue = selectedMedications.value[docId];
  if (currentValue === therapyName) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearMedicationSelection(docId);
  } else {
    // Select the medication
    selectedMedications.value[docId] = therapyName;
  }
};

const clearInsuranceSelection = (docId) => {
  delete selectedInsuranceCards.value[docId];
};

const clearMedicationSelection = (docId) => {
  delete selectedMedications.value[docId];
};

const openPreview = async (doc) => {
  if (!doc.id) {
    return;
  }

  showPreviewDialog.value = true;
  previewLoading.value = true;
  previewError.value = false;
  previewDocumentUrl.value = null;

  try {
    // Fetch PDF as blob following codebase pattern (handles auth automatically)
    const response = await intakeService.getDocumentPreview(doc.id);
    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    previewDocumentUrl.value = URL.createObjectURL(pdfBlob);
  } catch (error) {
    previewError.value = true;
  } finally {
    previewLoading.value = false;
  }
};

const closePreview = () => {
  showPreviewDialog.value = false;
  if (previewDocumentUrl.value) {
    URL.revokeObjectURL(previewDocumentUrl.value);
  }
  previewDocumentUrl.value = null;
  previewError.value = false;
};

// State abbreviation to full name mapping
const stateAbbrevToFullName = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

// Available gender options for OrderInfo
const validGenderOptions = ["Male", "Female", "Unspecified"];

const genderAbbrevToFull = { M: "Male", F: "Female" };

/**
 * Normalizes gender: M/F (case-insensitive) → Male/Female; full names matched case-insensitively return canonical option.
 */
const normalizeGender = (value) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  if (genderAbbrevToFull[upper]) return genderAbbrevToFull[upper];
  const canonical = validGenderOptions.find((opt) => opt.toLowerCase() === trimmed.toLowerCase());
  return canonical ?? null;
};

// Available state values from dropdown
const validStateValues = usaStatesListItems.map((item) => item.value);

/**
 * Normalizes state: full valid name (ignore case) is returned as canonical full name; abbreviation is converted to full name.
 */
const convertStateToFullName = (stateValue) => {
  if (!stateValue) return null;
  const trimmed = stateValue.trim();
  const lower = trimmed.toLowerCase();
  const fullNameMatch = validStateValues.find((v) => v.toLowerCase() === lower);
  if (fullNameMatch) return fullNameMatch;
  const upperAbbrev = trimmed.toUpperCase();
  return stateAbbrevToFullName[upperAbbrev] || null;
};

/**
 * Validates if a value is in the list of valid options
 */
const isValidOption = (value, validOptions) => {
  if (!value) return false;
  return validOptions.includes(value.trim());
};

/**
 * Parses BIN and PCN from insurance provider string and returns cleaned provider name
 * Format: "11 - PBM: Medi-Cal Magellan (MC) | BIN: 022659 | PCN: 6334225"
 * Returns: "11 - PBM: Medi-Cal Magellan (MC)"
 */
const parseInsuranceProviderForBinPcn = (insuranceProvider, orderInfo) => {
  if (!insuranceProvider || typeof insuranceProvider !== "string") {
    return insuranceProvider;
  }

  // Extract the part before the first "|" as the insurance provider name
  const providerName = insuranceProvider.split("|")[0].trim();

  // Regex to match BIN: followed by digits
  const binMatch = insuranceProvider.match(/BIN:\s*(\d+)/i);
  if (binMatch && binMatch[1]) {
    const binValue = binMatch[1].trim();
    if (binValue && !orderInfo.bin) {
      orderInfo.bin = binValue;
    }
  }

  // Regex to match PCN: followed by alphanumeric characters
  const pcnMatch = insuranceProvider.match(/PCN:\s*(\S+)/i);
  if (pcnMatch && pcnMatch[1]) {
    const pcnValue = pcnMatch[1].trim();
    if (pcnValue && !orderInfo.processorControlNumber) {
      orderInfo.processorControlNumber = pcnValue;
    }
  }

  return providerName;
};

/**
 * Looks up medication using NDC or medication name with optional strength/dosage
 * Relies on backend token-based search - sends full query and lets backend handle tokenization
 * Returns the best matching medication or null
 */
const lookupMedication = async (medicationName, ndc, strength = null) => {
  if (!medicationName) {
    return null;
  }

  try {
    let results = [];

    // 1. If NDC is available, lookup by NDC first
    if (ndc && /^\d{11}$/.test(ndc.replace(/-/g, ""))) {
      const cleanNdc = ndc.replace(/-/g, "");
      try {
        results = await mainServices.searchMedications(cleanNdc);
        if (results && results.length > 0) {
          // If we found results by NDC, return the first one
          return results[0];
        }
      } catch (error) {
        // NDC lookup failed, continue to name search
      }
    }

    // 2. Build search query: include medication name and strength if available
    // Backend will split into tokens and use AND logic, so all tokens must match
    const medicationNameQuery = medicationName.trim();
    let searchQuery = medicationNameQuery;
    if (strength) {
      searchQuery = `${searchQuery} ${strength}`.trim();
    }

    // 3. Send full query to backend - backend handles token-based search
    // First try with strength/dosage if available (for precise matching)
    if (searchQuery.length >= 3) {
      try {
        results = await mainServices.searchMedications(searchQuery);
        // Ensure results is an array
        if (!Array.isArray(results)) {
          results = [];
        }
      } catch (error) {
        // Search failed, try without strength
        results = [];
      }
    } else {
      return null;
    }

    // 4. If no results with strength/dosage, try again without it
    // This handles cases where dosage is quantity (e.g., "50 tablets") not strength
    if ((!results || results.length === 0) && strength && medicationNameQuery.length >= 3) {
      try {
        results = await mainServices.searchMedications(medicationNameQuery);
        // Ensure results is an array
        if (!Array.isArray(results)) {
          results = [];
        }
      } catch (error) {
        // Search failed, return null
        return null;
      }
    }

    if (!results || results.length === 0) {
      return null;
    }

    // 5. If one medication is returned, select it
    if (results.length === 1) {
      return results[0];
    }

    // 6. If multiple results, return first one
    // This ensures we use the backend's best match rather than falling back to intake medication
    return results[0];
  } catch (error) {
    // Lookup failed, return null
    return null;
  }
};

/**
 * Prepares OrderInfo object from selected intake data
 * Only maps fields if they exist in source (doesn't overwrite with null)
 * Validates dropdown/radio fields and skips if value doesn't match available options
 */
const prepopulateOrderInfo = async () => {
  const orderInfo = {};

  // 1. Selected IntakeDocuments → patientChart
  if (intakeRecords.value?.documents && selectedDocuments.value.length > 0) {
    orderInfo.patientChart = [];
    intakeRecords.value.documents.forEach((doc) => {
      if (selectedDocuments.value.includes(doc.id)) {
        // Use document key + timestamp for label
        const label = doc.key
          ? `${doc.key} - ${doc.createdOn ? new Date(doc.createdOn).toLocaleString() : "Intake Document"}`
          : `Intake Document - ${doc.createdOn ? new Date(doc.createdOn).toLocaleString() : ""}`;

        const links = doc.inputPresignedUrl ? [doc.inputPresignedUrl] : [];

        if (links.length > 0 || doc.key) {
          orderInfo.patientChart.push({ label, links });
        }
      }
    });
  }

  // 2. Selected Patient → OrderInfo Patient Fields
  if (selectedPatient.value) {
    const patient = selectedPatient.value;
    if (patient.firstName) orderInfo.patientFirstName = patient.firstName;
    if (patient.lastName) orderInfo.patientLastName = patient.lastName;
    if (patient.dob) orderInfo.patientDob = patient.dob;
    if (patient.externalId) orderInfo.patientExternalId = patient.externalId;
    if (patient.phoneNumber || patient.extraData?.phoneNumber)
      orderInfo.patientPhone = patient.phoneNumber ? patient.phoneNumber : patient.extraData?.phoneNumber;
    if (patient.address || patient.extraData?.patientAddress1)
      orderInfo.patientAddress1 = patient.address ? patient.address : patient.extraData?.patientAddress1;
    if (patient.extraData?.patientCity) orderInfo.patientCity = patient.extraData?.patientCity;
    if (patient.extraData?.patientState) {
      const fullStateName = convertStateToFullName(patient.extraData.patientState);
      if (fullStateName) {
        orderInfo.patientState = fullStateName;
      }
    }
    if (patient.extraData?.patientZip) orderInfo.patientZip = patient.extraData?.patientZip;
    // TODO revert back to patientGender once mapping is updated
    if (patient.extraData?.gender) {
      const genderValue = normalizeGender(patient.extraData.gender);
      if (genderValue) orderInfo.patientGender = genderValue;
    }
    if (patient.extraData?.providerContactFax) orderInfo.providerContactFax = patient.extraData.providerContactFax;
    if (patient.extraData?.providerNpiNumber) orderInfo.providerNpiNumber = patient.extraData.providerNpiNumber;
    if (patient.extraData?.providerZip) orderInfo.providerZip = patient.extraData.providerZip;
    if (patient.extraData?.insuranceProvider) orderInfo.insuranceProvider = patient.extraData.insuranceProvider;
    if (patient.extraData?.providerCity) orderInfo.providerCity = patient.extraData.providerCity;
    if (patient.extraData?.providerLastName) orderInfo.providerLastName = patient.extraData.providerLastName;
    if (patient.extraData?.providerFirstName) orderInfo.providerFirstName = patient.extraData.providerFirstName;
    if (patient.extraData?.providerContactPhone)
      orderInfo.providerContactPhone = patient.extraData.providerContactPhone;
    if (patient.extraData?.providerState) {
      const fullStateName = convertStateToFullName(patient.extraData.providerState);
      if (isValidOption(fullStateName, validStateValues)) {
        orderInfo.providerState = fullStateName;
      }
    }
    if (patient.extraData?.providerAddress1) orderInfo.providerAddress1 = patient.extraData.providerAddress1;
    if (patient.extraData?.processorControlNumber)
      orderInfo.processorControlNumber = patient.extraData.processorControlNumber;
    if (patient.extraData?.bin) orderInfo.bin = patient.extraData.bin;
    if (patient.extraData?.grpNumber) orderInfo.grpNumber = patient.extraData.grpNumber;
    if (patient.extraData?.patientMemberId) orderInfo.patientMemberId = patient.extraData.patientMemberId;
    if (patient.extraData?.insuranceProvider) orderInfo.insuranceProvider = patient.extraData.insuranceProvider;
  }

  // 3. Selected IntakeInsuranceCard → OrderInfo Insurance Fields
  if (intakeRecords.value?.documents) {
    intakeRecords.value.documents.forEach((doc) => {
      const selectedCardId = selectedInsuranceCards.value[doc.id];
      if (selectedCardId && doc.insuranceCards) {
        const card = doc.insuranceCards.find((c) => c.id === selectedCardId);
        if (card) {
          if (card.memberId) orderInfo.patientMemberId = card.memberId;
          if (card.payorName) {
            // Parse BIN and PCN from payorName and get cleaned provider name (first part before "|")
            orderInfo.insuranceProvider = parseInsuranceProviderForBinPcn(card.payorName, orderInfo);
          }
          if (card.insurancePlan) orderInfo.insurancePlanType = card.insurancePlan;
          // Map benefitType: MEDICAL -> medical_benefits, PHARMACY -> pharmacy_benefits
          if (card.benefitType) {
            const benefitTypeMap = {
              MEDICAL: "medical_benefits",
              PHARMACY: "pharmacy_benefits",
            };
            orderInfo.benefitType = Object.keys(benefitTypeMap).includes(card.benefitType)
              ? benefitTypeMap[card.benefitType]
              : "pharmacy_benefits";
          }
          // Only set from card fields if not already parsed from payorName
          if (card.rxBin && !orderInfo.bin) orderInfo.bin = card.rxBin;
          if (card.rxPcn && !orderInfo.processorControlNumber) orderInfo.processorControlNumber = card.rxPcn;
          if (card.rxGrp && !orderInfo.grpNumber) orderInfo.grpNumber = card.rxGrp;
          if (card.Group && !orderInfo.grpNumber) orderInfo.grpNumber = card.Group;
        }
      }
    });
  }

  // Default benefitType to "pharmacy_benefits" if not set or no insurance card selected
  if (!orderInfo.benefitType) {
    orderInfo.benefitType = "pharmacy_benefits";
  }

  // 4. Selected therapyAdministration → OrderInfo Medication Fields
  // 5. Selected IntakeOrderForm (containing medication) → OrderInfo Other Fields
  if (intakeRecords.value?.documents) {
    for (const doc of intakeRecords.value.documents) {
      const selectedMedicationName = selectedMedications.value[doc.id];
      if (selectedMedicationName && doc.orderForms) {
        // Find the order form containing the selected medication
        let selectedOrderForm = null;
        let selectedTherapy = null;

        for (const form of doc.orderForms) {
          if (form.therapyAdministration) {
            selectedTherapy = form.therapyAdministration.find((therapy) => therapy.name === selectedMedicationName);
            if (selectedTherapy) {
              selectedOrderForm = form;
              break;
            }
          }
        }

        // Map medication fields from therapyAdministration
        if (selectedTherapy) {
          const medicationName = selectedTherapy.name;
          const ndc = selectedTherapy.nationalDrugCode || selectedTherapy.ndcNumber;
          // Get strength/dosage for more precise matching
          const strength = selectedTherapy.strength || selectedTherapy.dosage || null;

          // Lookup medication to get standardized medication details
          const lookedUpMedication = await lookupMedication(medicationName, ndc, strength);
          if (lookedUpMedication && lookedUpMedication.medication) {
            // Use looked up medication details - prioritize these over intake values
            orderInfo.medication = lookedUpMedication.medication;
            if (lookedUpMedication.strength) {
              orderInfo.strength = lookedUpMedication.strength;
            }
            if (lookedUpMedication.routeAdministration) {
              orderInfo.routeAdministration = lookedUpMedication.routeAdministration;
            }
            if (lookedUpMedication.nationalDrugCode) {
              orderInfo.nationalDrugCode = lookedUpMedication.nationalDrugCode;
            }
            if (lookedUpMedication.name) {
              const medicationDefaults = getMedicationDefaultsForName(
                lookedUpMedication.name,
                requirementsStore.clientConfig?.priorAuth?.medicationDefaults
              );
              // Apply medicationDefaults from client config when medication name matches
              if (medicationDefaults) {
                applyMedicationDefaultsToTarget(medicationDefaults, orderInfo);
              }
            }
          } else if (medicationName) {
            // Fallback to original medication name only if lookup completely failed
            orderInfo.medication = medicationName;
          }

          // Use therapy values if not already set from lookup
          if (selectedTherapy.strength && !orderInfo.strength) {
            orderInfo.strength = selectedTherapy.strength;
          }
          if (selectedTherapy.route && !orderInfo.routeAdministration) {
            orderInfo.routeAdministration = selectedTherapy.route;
          } else if (selectedTherapy.deliveryType && !orderInfo.routeAdministration) {
            orderInfo.routeAdministration = selectedTherapy.deliveryType;
          } else if (selectedTherapy.routeAdministration && !orderInfo.routeAdministration) {
            orderInfo.routeAdministration = selectedTherapy.routeAdministration;
          }
          if (selectedTherapy.dosage) {
            orderInfo.dailyDosage = selectedTherapy.dosage;
          } else if (selectedTherapy.dailyDosage) {
            orderInfo.dailyDosage = selectedTherapy.dailyDosage;
          }
          if (selectedTherapy.nationalDrugCode && !orderInfo.nationalDrugCode) {
            orderInfo.nationalDrugCode = selectedTherapy.nationalDrugCode;
          } else if (selectedTherapy.ndcNumber && !orderInfo.nationalDrugCode) {
            orderInfo.nationalDrugCode = selectedTherapy.ndcNumber;
          }

          // Frequency: frequencyValue/frequencyUnit take precedence
          if (selectedTherapy.frequencyValue && selectedTherapy.frequencyUnit) {
            orderInfo.frequency = `${selectedTherapy.frequencyValue} ${selectedTherapy.frequencyUnit}`;
          } else if (selectedTherapy.frequency) {
            orderInfo.frequency = selectedTherapy.frequency;
          }

          // Expected length of therapy
          if (selectedTherapy.expectedLengthOfTherapy) {
            orderInfo.expectedLengthOfTherapy = selectedTherapy.expectedLengthOfTherapy;
          } else if (selectedTherapy.durationValue && selectedTherapy.durationUnit) {
            orderInfo.expectedLengthOfTherapy = `${selectedTherapy.durationValue} ${selectedTherapy.durationUnit}`;
          }

          if (selectedTherapy.quantityPer30Days) {
            orderInfo.quantityPer30Days = selectedTherapy.quantityPer30Days;
          }
        }

        // Map order form fields
        if (selectedOrderForm) {
          // Provider fields
          if (selectedOrderForm.providerFirstName) orderInfo.providerFirstName = selectedOrderForm.providerFirstName;
          if (selectedOrderForm.providerLastName) orderInfo.providerLastName = selectedOrderForm.providerLastName;
          if (selectedOrderForm.providerTitle) orderInfo.providerTitle = selectedOrderForm.providerTitle;
          if (selectedOrderForm.providerNpi) orderInfo.providerNpiNumber = selectedOrderForm.providerNpi;

          // Remove extension from phone/fax
          if (selectedOrderForm.providerFax) {
            orderInfo.providerContactFax = selectedOrderForm.providerFax.replace(/\s*ext\.?\s*\d+$/i, "").trim();
          }
          if (selectedOrderForm.providerPhone) {
            orderInfo.providerContactPhone = selectedOrderForm.providerPhone.replace(/\s*ext\.?\s*\d+$/i, "").trim();
          }

          if (selectedOrderForm.providerAddress) orderInfo.providerAddress1 = selectedOrderForm.providerAddress;
          if (selectedOrderForm.providerCity) orderInfo.providerCity = selectedOrderForm.providerCity;
          if (selectedOrderForm.providerState) {
            const fullStateName = convertStateToFullName(selectedOrderForm.providerState);
            if (fullStateName && isValidOption(fullStateName, validStateValues)) {
              orderInfo.providerState = fullStateName;
            }
          }
          if (selectedOrderForm.providerZip) orderInfo.providerZip = selectedOrderForm.providerZip;
          if (selectedOrderForm.providerPracticeName)
            orderInfo.providerOfficeName = selectedOrderForm.providerPracticeName;

          // Patient demographics from order form
          if (selectedOrderForm.patientGender) {
            if (isValidOption(selectedOrderForm.patientGender, validGenderOptions)) {
              orderInfo.patientGender = selectedOrderForm.patientGender.trim();
            }
            // Skip if gender doesn't match available options
          }
          if (selectedOrderForm.patientAddress) orderInfo.patientAddress1 = selectedOrderForm.patientAddress;
          if (selectedOrderForm.patientCity) orderInfo.patientCity = selectedOrderForm.patientCity;
          if (selectedOrderForm.patientState) {
            const fullStateName = convertStateToFullName(selectedOrderForm.patientState);
            if (fullStateName && isValidOption(fullStateName, validStateValues)) {
              orderInfo.patientState = fullStateName;
            }
          }
          if (selectedOrderForm.patientZip) orderInfo.patientZip = selectedOrderForm.patientZip;

          // Combine weight value and unit into string
          if (selectedOrderForm.patientWeightValue || selectedOrderForm.patientWeightUnit) {
            const weightParts = [];
            if (selectedOrderForm.patientWeightValue) weightParts.push(selectedOrderForm.patientWeightValue);
            if (selectedOrderForm.patientWeightUnit) weightParts.push(selectedOrderForm.patientWeightUnit);
            orderInfo.patientWeight = weightParts.join(" ");
          }

          if (selectedOrderForm.patientAllergies) orderInfo.patientDrugAllergies = selectedOrderForm.patientAllergies;
          if (selectedOrderForm.diagnosisIcd10Code) {
            orderInfo.primaryDiagnosisIcd10Code = selectedOrderForm.diagnosisIcd10Code;
            // Fetch diagnosis name from ICD10 code
            const diagnosisName = await fetchDiagnosisNameFromICD10(selectedOrderForm.diagnosisIcd10Code);
            if (diagnosisName) {
              orderInfo.primaryDiagnosisName = diagnosisName;
            }
          }
          if (selectedOrderForm.dateOfOrder) orderInfo.treatmentStartDate = selectedOrderForm.dateOfOrder;
        }
      }
    }
  }

  return orderInfo;
};

const handlePrepopulate = async () => {
  const prepopulatedData = await prepopulateOrderInfo();
  emit("prepopulate-order-info", prepopulatedData);
  clearSelections();
};

// Watch for patient selection changes and fetch intake records
watch(selectedPatientId, (newPatientId) => {
  if (newPatientId) {
    fetchIntakeRecords(newPatientId);
  } else {
    intakeRecords.value = null;
    clearSelections();
  }
});

defineExpose({
  selectedPatient,
  selectedDocuments,
  selectedInsuranceCards,
  selectedMedications,
  intakeRecords,
  clearSelection,
  resetSearch,
  prepopulateOrderInfo,
});
</script>

<style lang="scss" scoped></style>
